import { useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { rejectedChanges, retryRejected, type RejectedChange } from '../sync/engine';
import { useToast } from '../ui/Toast';

/**
 * Cambios que el servidor rechazó (normalmente por el límite del plan). Siguen en este
 * dispositivo pero no se sincronizan; tras resolver la causa se pueden reenviar.
 */
export function RejectedPanel({ count }: { count: number }) {
  const { t } = useI18n();
  const toast = useToast();
  const [items, setItems] = useState<RejectedChange[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    void rejectedChanges().then((r) => alive && setItems(r));
    return () => {
      alive = false;
    };
  }, [count]);

  const reason = (r: RejectedChange) => {
    if (r.code === 'plan_limit') return t('sync.rejectedReason.plan_limit');
    if (r.code === 'invalid') return t('sync.rejectedReason.invalid');
    return t('sync.rejectedReason.other');
  };

  return (
    <div className="banner" role="alert">
      <div className="banner__body">
        <strong>{t('sync.rejected', { count })}</strong>
        <span className="small">{t('sync.rejectedHelp')}</span>
        <ul className="small" style={{ margin: 0, paddingLeft: '1.2em' }}>
          {items.slice(0, 5).map((r) => (
            <li key={`${r.entity}:${r.id}`}>
              {r.name ?? t(`sync.rejectedEntity.${r.entity}`)}: {reason(r)}
            </li>
          ))}
        </ul>
        {items.length > 5 && <span className="small">{t('sync.rejectedMore', { count: items.length - 5 })}</span>}
        <div className="row">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const n = await retryRejected();
                toast.show(t('sync.rejectedRetried', { count: n }));
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('sync.rejectedRetry')}
          </button>
        </div>
      </div>
    </div>
  );
}
